import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiRequest, withApi } from "../utils/api";

// RRHH: lista de empleados (GET /employees, rol hr).
export const fetchAdminEmployees = createAsyncThunk(
  "admin/fetchAdminEmployees",
  withApi(async (_, { token }) => {
    const data = await apiRequest("/employees", { token });
    return Array.isArray(data) ? data : [];
  })
);

// RRHH: edita un empleado (PATCH /employees/:id). `employeeData` debe cumplir UpdateEmployeeDto.
export const updateAdminEmployee = createAsyncThunk(
  "admin/updateAdminEmployee",
  withApi(async ({ id, employeeData }, { token }) => {
    const { data, message } = await apiRequest(`/employees/${id}`, {
      method: "PATCH",
      body: employeeData,
      token,
      withMessage: true,
    });
    return {
      message: message || "Empleado actualizado",
      updatedEmployee: data,
      submittedData: employeeData,
      id,
    };
  })
);

// Admin: permisos del empleado (PATCH /employees/:id/roles).
export const updateEmployeeRoles = createAsyncThunk(
  "admin/updateEmployeeRoles",
  withApi(async ({ id, roles }, { token }) => {
    const { data, message } = await apiRequest(`/employees/${id}/roles`, {
      method: "PATCH",
      body: { roles },
      token,
      withMessage: true,
    });
    return {
      message: message || "Permisos actualizados",
      updatedEmployee: data,
      submittedData: { roles },
      id,
    };
  })
);

const mergeEmployee = (state, payload) => {
  const updated = payload.updatedEmployee || payload.submittedData;
  const employeeId = payload.updatedEmployee?._id || payload.id;
  if (!employeeId) return;
  const index = state.employees.findIndex((emp) => emp._id === employeeId);
  if (index !== -1) {
    state.employees[index] = { ...state.employees[index], ...updated };
  }
};

const adminSlice = createSlice({
  name: "admin",
  initialState: {
    employees: [],
    employeesStatus: "idle",
    employeesError: null,
    updateStatus: "idle",
    updateError: null,
    updateMessage: null,
  },
  reducers: {
    clearAdminUpdateFeedback(state) {
      state.updateStatus = "idle";
      state.updateError = null;
      state.updateMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminEmployees.pending, (state) => {
        state.employeesStatus = "loading";
        state.employeesError = null;
      })
      .addCase(fetchAdminEmployees.fulfilled, (state, action) => {
        state.employeesStatus = "succeeded";
        state.employees = action.payload;
      })
      .addCase(fetchAdminEmployees.rejected, (state, action) => {
        state.employeesStatus = "failed";
        state.employeesError = action.payload || "Error al cargar empleados";
      })
      .addCase(updateAdminEmployee.pending, (state) => {
        state.updateStatus = "loading";
        state.updateError = null;
        state.updateMessage = null;
      })
      .addCase(updateAdminEmployee.fulfilled, (state, action) => {
        state.updateStatus = "succeeded";
        state.updateError = null;
        state.updateMessage = action.payload.message;
        mergeEmployee(state, action.payload);
      })
      .addCase(updateAdminEmployee.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.updateError = action.payload || "Error al actualizar empleado";
      })
      .addCase(updateEmployeeRoles.pending, (state) => {
        state.updateStatus = "loading";
        state.updateError = null;
        state.updateMessage = null;
      })
      .addCase(updateEmployeeRoles.fulfilled, (state, action) => {
        state.updateStatus = "succeeded";
        state.updateError = null;
        state.updateMessage = action.payload.message;
        mergeEmployee(state, action.payload);
      })
      .addCase(updateEmployeeRoles.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.updateError = action.payload || "Error al actualizar permisos";
      });
  },
});

export const { clearAdminUpdateFeedback } = adminSlice.actions;
export default adminSlice.reducer;
